import React from "react";
import { Flex, theme } from "antd";
import { useTranslate } from "@refinedev/core";
import { TrashIcon } from "@phosphor-icons/react";
import { IconButton, radius } from "@libertify/ui";
import { DocumentFile } from "../types";
import { FileIdentity } from "./FileIdentity";
import { FileVisibilityToggle } from "./FileVisibilityToggle";
import { PreviewButton } from "./PreviewButton";

interface DocumentFileRowProps {
  file: DocumentFile;
  onPreview: () => void;
  onToggleHidden: () => void;
  onRemove: () => void;
  /** Hide the eye icon — there's nothing to toggle when this is the last
   *  remaining visible document (an experience always needs a visible main doc). */
  showVisibilityToggle?: boolean;
}

/** A single uploaded document: identity + preview, visibility and remove actions. */
export const DocumentFileRow: React.FC<DocumentFileRowProps> = ({
  file,
  onPreview,
  onToggleHidden,
  onRemove,
  showVisibilityToggle = true,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();

  return (
    <Flex
      align="center"
      gap={12}
      style={{
        padding: "10px 12px",
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: radius.box,
      }}
    >
      <FileIdentity file={file} showHiddenBadge />
      <PreviewButton onClick={onPreview} />
      {showVisibilityToggle ? (
        <FileVisibilityToggle hidden={file.hidden} onToggle={onToggleHidden} />
      ) : null}
      <IconButton
        variant="danger"
        size="s"
        aria-label={t("createExperience.addOns.documents.removeFile")}
        icon={<TrashIcon size={15} />}
        onClick={onRemove}
      />
    </Flex>
  );
};
